import { CancellationStepContentProps } from "./types";
import {
  WSElement,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";

export const PGLICancellationAreYouSureInfo: React.FC<
  CancellationStepContentProps
> = ({ planType }) => (
  <WSList gap="M">
    <WSElement>
      <WSText.Paragraph weight="medium" mb="M">
        Are you sure you want to cancel?
      </WSText.Paragraph>
      <WSText.ParagraphSm color="gray500">
        Please review the information about Wingspan Professional & General
        Liability Insurance and the impact assessment for cancelling the
        insurance below before proceeding.
      </WSText.ParagraphSm>
    </WSElement>

    <WSInfoBox icon="bulb">
      <WSText.ParagraphSm color="gray500">
        This master policy combines two essential coverages into one streamlined
        package, helping you meet client requirements and protect your business
        instantly. It includes:
        <ul style={{ paddingTop: "1rem" }}>
          <li style={{ marginBottom: "1rem" }}>
            <b>Professional Liability (Errors & Omissions)</b>: Protects you if
            a client claims your professional mistakes, negligence, or failure
            to deliver services caused them a financial loss. This covers legal
            defense costs and potential settlements.
          </li>
          <li>
            <b>General Liability</b>: Covers claims of third-party bodily injury
            (e.g., a slip-and-fall at an inspection site) or property damage
            caused by your business operations.
          </li>
        </ul>
      </WSText.ParagraphSm>
    </WSInfoBox>

    <WSInfoBox
      title="Impact assessment"
      icon="alert-circle-fill"
      colorBackground="amber50"
    >
      <WSText.ParagraphSm color="gray600">
        Remember, having professional and general liability is a critical layer
        of protection for your business — especially for independent contractors
        whose business face potential claims for errors and omissions as well as
        general injury or damage. By cancelling this enrollment, you'd be
        cancelling both Professional Liability Insurance and General Liability
        Insurance and working without coverage.
      </WSText.ParagraphSm>
      <WSText.ParagraphSm color="gray600">
        You may be financially responsible for:
      </WSText.ParagraphSm>
      <WSElement as="ul">
        <li>
          Client claims on your professional mistakes, negligence, or failure to
          deliver services caused them a financial loss.
        </li>
        <li>Cost of legal defense costs and potential settlements.</li>
        <li>
          Claims of third-party bodily injury (e.g., a slip-and-fall at an
          inspection site) or property damage caused by your business
          operations.
        </li>
      </WSElement>
      <WSText.ParagraphSm color="gray600">
        Accidents don’t wait for coverage to be required — they happen when you
        least expect it. Canceling your protection removes one of the few
        systems in place to support you in case something goes wrong. Please
        review how this could affect your protection and peace of mind before
        proceeding.
      </WSText.ParagraphSm>
    </WSInfoBox>
  </WSList>
);

export const PGLICancellationContinuingInfo: React.FC<
  CancellationStepContentProps
> = ({ planType }) => (
  <WSList gap="M">
    <WSElement>
      <WSText.Paragraph weight="medium" mb="M">
        Continue with cancellation
      </WSText.Paragraph>
      <WSText.ParagraphSm color="gray500">
        If you wish to proceed and submit a cancellation, here’s what to expect:
      </WSText.ParagraphSm>
    </WSElement>
    <WSInfoBox title="Submit a cancellation" icon="calendar">
      Your cancellation takes effect at the end of the paid period. If you
      decide to proceed to submit a cancellation, you will continue to be
      covered during this period.
    </WSInfoBox>

    <WSInfoBox title="Can I re-enroll?" icon="bulb">
      Yes — re-enrollment is possible if your coverage has been cancelled.
      However, any gap in coverage may impact your compliance and could affect
      your ability to perform client work during that period. We recommend
      reviewing your impact assessment and checking your client requirements
      before proceeding.
    </WSInfoBox>
  </WSList>
);

export const PGLICancellationConfirmationInfo: React.FC<
  CancellationStepContentProps
> = ({ planType }) => (
  <WSList gap="M">
    <WSElement>
      <WSText.Paragraph weight="medium" mb="M">
        Confirm & submit cancellation
      </WSText.Paragraph>
      <WSText.ParagraphSm color="gray500">
        You're about to cancel your coverage. Please confirm that you understand
        the following:
      </WSText.ParagraphSm>
    </WSElement>

    <WSInfoBox icon="accept">
      <WSText.ParagraphSm color="gray500">
        I understand and agree that:
        <ul>
          <li>
            My coverage will remain active until the end of the paid period
            after cancellation is submitted, I will be covered—and premiums will
            be deducted—for all payments processed during this period.
          </li>
          <li>
            After the coverage is over, I will no longer be protected for
            professional and general liability claims.
          </li>
          <li>
            This change may affect my eligibility for certain client projects.
          </li>
        </ul>
      </WSText.ParagraphSm>
    </WSInfoBox>
  </WSList>
);
